import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

export default function EditKnowledgeData({
  knowledgeItem,
  onClose,
  onUpdate,
  chatbotId,
  permission,
}) {
  const [formData, setFormData] = useState({
    type: "",
    website_link: "",
    page_content: "",
    filename: "",
    image_content: "",
    content: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (knowledgeItem) {
      setFormData({
        type: knowledgeItem.type || "",
        website_link: knowledgeItem.website_link || "",
        page_content: knowledgeItem.page_content || "",
        filename: knowledgeItem.filename || "",
        image_content: knowledgeItem.image_content || "",
        content: knowledgeItem.content || "",
      });
    }
  }, [knowledgeItem]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
    // Clear messages when user starts typing
    setError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!permission?.canModify) {
      setError("You don't have permission to edit knowledge data.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const params = chatbotId ? { chatbot_id: chatbotId } : {};

      // Prepare update payload based on knowledge type
      let updatePayload = {};

      switch (formData.type) {
        case "website_link":
          updatePayload = {
            website_link: formData.website_link,
            page_content: formData.page_content,
          };
          break;
        case "document":
          updatePayload = {
            filename: formData.filename,
            page_content: formData.page_content,
          };
          break;
        case "image":
          updatePayload = {
            filename: formData.filename,
            image_content: formData.image_content || formData.content,
          };
          break;
        case "voice":
          updatePayload = {
            filename: formData.filename,
            content: formData.content,
          };
          break;
        default:
          throw new Error("Invalid knowledge type");
      }

      await axiosInstance.put(
        `/update-knowleged/${formData.type}/${knowledgeItem.id}`,
        updatePayload,
        { params }
      );

      setSuccess("Knowledge data updated successfully!");
      setTimeout(() => {
        onUpdate();
      }, 1500);
    } catch (error) {
      console.error("Error updating knowledge data:", error);
      setError(
        error.response?.data?.message ||
          "Failed to update knowledge data. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const renderFormFields = () => {
    switch (formData.type) {
      case "website_link":
        return (
          <>
            <TextField
              fullWidth
              label="Website Link"
              value={formData.website_link}
              onChange={handleChange("website_link")}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Page Content"
              value={formData.page_content}
              onChange={handleChange("page_content")}
              margin="normal"
              multiline
              rows={6}
              required
              disabled={loading}
            />
          </>
        );

      case "document":
        return (
          <>
            <TextField
              fullWidth
              label="Filename"
              value={formData.filename}
              onChange={handleChange("filename")}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Page Content"
              value={formData.page_content}
              onChange={handleChange("page_content")}
              margin="normal"
              multiline
              rows={6}
              required
              disabled={loading}
            />
          </>
        );

      case "image":
        return (
          <>
            <TextField
              fullWidth
              label="Filename"
              value={formData.filename}
              onChange={handleChange("filename")}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Image Content"
              value={formData.image_content || formData.content}
              onChange={handleChange("image_content")}
              margin="normal"
              multiline
              rows={6}
              disabled={loading}
            />
          </>
        );

      case "voice":
        return (
          <>
            <TextField
              fullWidth
              label="Filename"
              value={formData.filename}
              onChange={handleChange("filename")}
              margin="normal"
              required
              disabled={loading}
            />
            <TextField
              fullWidth
              label="Content"
              value={formData.content}
              onChange={handleChange("content")}
              margin="normal"
              multiline
              rows={6}
              required
              disabled={loading}
            />
          </>
        );

      default:
        return (
          <Alert severity="warning">
            Unknown knowledge type: {formData.type}
          </Alert>
        );
    }
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5" fontWeight={700} color="textPrimary">
          Edit Knowledge Data
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
          {success}
        </Alert>
      )}

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{ maxHeight: "calc(90vh - 180px)", overflow: "auto", pr: 1 }}
      >
        <TextField
          fullWidth
          label="Data Type"
          value={formData.type}
          margin="normal"
          disabled
          sx={{
            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: "text.primary",
              opacity: 0.7,
            },
          }}
        />

        {renderFormFields()}

        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
            disabled={loading || !permission?.canModify}
            fullWidth
          >
            {loading ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={onClose}
            disabled={loading}
            fullWidth
          >
            Cancel
          </Button>
        </Stack>

        {!permission?.canModify && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You don't have permission to edit knowledge data.
          </Alert>
        )}
      </Box>
    </Box>
  );
}